import type { DatoxArtifact, DatoxDashboard } from "./api"

export type ReportApprovalScope = {
  projectID: string
  periodID: string | null
  caseID?: string | null
  workspaceID?: string
  targetID?: string
  dashboardID?: string
  clientID?: string | null
  targetCount: number
  reportIDs: string[]
}

export function clientApprovalArtifacts(artifacts: DatoxArtifact[], scope: ReportApprovalScope) {
  if (!scope.targetID) return []
  return artifacts.filter((artifact) => {
    if (artifact.project_id !== scope.projectID || (artifact.period_id ?? null) !== scope.periodID ||
      (artifact.case_id ?? null) !== (scope.caseID ?? null) || artifact.artifact_type !== "report" ||
      ["deleted", "superseded"].includes(artifact.status)) return false
    const metadata = artifact.artifact_metadata
    if (metadata.source === "dashboard_share") return false
    if (scope.workspaceID && metadata.workspace_id && metadata.workspace_id !== scope.workspaceID) return false
    const linked = (scope.workspaceID && metadata.workspace_id === scope.workspaceID) || scope.reportIDs.includes(artifact.id) ||
      (scope.dashboardID && metadata.dashboard_id === scope.dashboardID)
    if (!linked) return false
    if (metadata.client_id != null && scope.clientID != null && String(metadata.client_id) !== scope.clientID) return false
    if (metadata.factsheet_target_id != null) return metadata.factsheet_target_id === scope.targetID
    if (metadata.dashboard_id != null) return metadata.dashboard_id === scope.dashboardID
    if (metadata.client_id != null && scope.clientID != null) return String(metadata.client_id) === scope.clientID
    // Older single-target workspaces did not stamp a target on their reports.
    // Multi-client reports must have an explicit matching identity.
    return !!scope.workspaceID && metadata.workspace_id === scope.workspaceID && scope.targetCount === 1
  }).sort((left, right) => right.updated_at.localeCompare(left.updated_at) || left.id.localeCompare(right.id))
}

export function reportShareArtifacts(dashboard: Pick<DatoxDashboard, "id" | "company_id" | "project_id" | "period_id" | "case_id">, artifacts: DatoxArtifact[]) {
  return artifacts.filter((artifact) => artifact.company_id === dashboard.company_id && artifact.project_id === dashboard.project_id &&
    (artifact.period_id ?? null) === (dashboard.period_id ?? null) && (artifact.case_id ?? null) === (dashboard.case_id ?? null) &&
    !["deleted", "superseded"].includes(artifact.status) && artifact.artifact_metadata.source === "dashboard_share" && artifact.artifact_metadata.dashboard_id === dashboard.id)
    .sort((left, right) => right.updated_at.localeCompare(left.updated_at) || left.id.localeCompare(right.id))
}

// The existing Approval view can present a saved dashboard before the first
// sharing artifact exists. This identifier is only a UI adapter, never posted
// to the report-artifact approval endpoint.
export function dashboardApprovalOption(dashboard: DatoxDashboard, title: string): DatoxArtifact {
  return { id: `dashboard:${dashboard.id}`, company_id: dashboard.company_id, project_id: dashboard.project_id,
    period_id: dashboard.period_id, case_id: dashboard.case_id, title, artifact_type: "report", format: "dashboard",
    status: dashboard.status, created_at: dashboard.created_at, updated_at: dashboard.updated_at,
    artifact_metadata: { source: "dashboard_share", dashboard_id: dashboard.id } }
}
